import { Container, Graphics, Text } from "pixi.js";
import { GRID_COLS, GRID_ROWS, type GridState } from "./grid";
import { GRAY_TTL } from "./grid";
import { GRAY_COLOR, type Palette } from "./palette";

const CELL_R = 11;
const CELL_SPACING = 30;
const ROW_SPACING = 56;

export class Hud {
  readonly container = new Container();
  private readonly scoreLabel: Text;
  private readonly scoreText: Text;
  private readonly songText: Text;
  private readonly waveform = new Graphics();
  private readonly waveMarker = new Graphics();
  private readonly gridG = new Graphics();
  private readonly btnUp = new Container();
  private readonly btnDown = new Container();
  private readonly floats: { t: Text; life: number; vy: number }[] = [];
  private wavePoints: number[] = [];
  private width = 0;
  private height = 0;

  onLaneChange: (dir: 1 | -1) => void = () => {};

  constructor(songName: string, intensity: Float32Array) {
    this.scoreLabel = new Text({
      text: "score",
      style: { fontFamily: "Montserrat, Arial", fontSize: 14, fill: 0xffffff, fontWeight: "600" },
    });
    this.scoreText = new Text({
      text: "0",
      style: { fontFamily: "Montserrat, Arial", fontSize: 34, fill: 0xffffff, fontWeight: "700" },
    });
    this.songText = new Text({
      text: songName,
      style: { fontFamily: "Montserrat, Arial", fontSize: 15, fill: 0xffffff, fontWeight: "600" },
    });
    this.scoreLabel.anchor.set(0.5, 0);
    this.scoreText.anchor.set(0.5, 0);
    this.songText.anchor.set(0.5, 0);

    // Downsample intensity to ~140 points for the progress waveform.
    const pts = 140;
    for (let i = 0; i < pts; i++) {
      const idx = Math.floor((i / (pts - 1)) * (intensity.length - 1));
      this.wavePoints.push(intensity[idx]!);
    }

    this.buildButton(this.btnUp, 1);
    this.buildButton(this.btnDown, -1);

    this.container.addChild(
      this.waveform,
      this.waveMarker,
      this.scoreLabel,
      this.scoreText,
      this.songText,
      this.gridG,
      this.btnUp,
      this.btnDown,
    );
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.scoreLabel.position.set(width / 2, 10);
    this.scoreText.position.set(width / 2, 26);
    this.songText.position.set(width / 2, 62);
    const btnMargin = 64;
    this.btnUp.position.set(btnMargin, height - btnMargin);
    this.btnDown.position.set(width - btnMargin, height - btnMargin);
    this.drawWaveform();
  }

  setScore(score: number): void {
    this.scoreText.text = score.toLocaleString("de-DE"); // 5.621-style separators
  }

  setProgress(t01: number): void {
    const { x0, y0, w, h } = this.waveBox();
    const i = Math.min(this.wavePoints.length - 1, Math.floor(t01 * this.wavePoints.length));
    const px = x0 + (i / (this.wavePoints.length - 1)) * w;
    const py = y0 + h - this.wavePoints[i]! * h;
    this.waveMarker.clear();
    this.waveMarker.moveTo(px, py - 14).lineTo(px - 5, py - 22).lineTo(px + 5, py - 22).lineTo(px, py - 14);
    this.waveMarker.fill({ color: 0xffffff });
  }

  renderGrid(grid: GridState, now: number, palette: Palette): void {
    const g = this.gridG;
    g.clear();
    const x0 = 34;
    const y0 = this.height / 2 - ROW_SPACING;
    for (let row = 0; row < GRID_ROWS; row++) {
      // Row 0 is the bottom lane — draw bottom row last (lowest on screen).
      const y = y0 + (GRID_ROWS - 1 - row) * ROW_SPACING;
      const cells = grid.rows[row]!;
      for (let col = 0; col < GRID_COLS; col++) {
        const x = x0 + col * CELL_SPACING;
        const cell = cells[col];
        g.circle(x, y, CELL_R).stroke({ width: 2.5, color: 0xffffff, alpha: 0.9 });
        if (cell) {
          if (cell.kind === "gray" && cell.expiresAt !== undefined) {
            const remain = Math.max(0, cell.expiresAt - now) / GRAY_TTL;
            g.circle(x, y, CELL_R - 3).fill({ color: GRAY_COLOR, alpha: 0.4 + 0.6 * remain });
            // moveTo first: otherwise the path connects from the previous
            // shape and draws a stray diagonal line into the arc start.
            g.moveTo(x, y - (CELL_R - 1));
            g.arc(x, y, CELL_R - 1, -Math.PI / 2, -Math.PI / 2 + remain * Math.PI * 2);
            g.stroke({ width: 2, color: 0xd0d4da, alpha: 0.9 });
          } else {
            // Cells resolve against the live palette — in unison with the sky.
            const color = cell.slot === "accent" ? palette.noteAccent : palette.notePrimary;
            g.circle(x, y, CELL_R - 3).fill({ color });
          }
        }
      }
    }
  }

  spawnFloat(message: string, x: number, y: number, color: number): void {
    const t = new Text({
      text: message,
      style: { fontFamily: "Montserrat, Arial", fontSize: 20, fill: color, fontWeight: "700" },
    });
    t.anchor.set(0.5);
    t.position.set(x, y);
    this.container.addChild(t);
    this.floats.push({ t, life: 1, vy: -46 });
  }

  update(dtSeconds: number): void {
    for (let i = this.floats.length - 1; i >= 0; i--) {
      const f = this.floats[i]!;
      f.life -= dtSeconds / 1.1;
      f.t.y += f.vy * dtSeconds;
      f.t.alpha = Math.max(0, f.life);
      if (f.life <= 0) {
        f.t.destroy();
        this.floats.splice(i, 1);
      }
    }
  }

  private waveBox() {
    return { x0: 24, y0: 30, w: Math.min(210, this.width * 0.22), h: 44 };
  }

  private drawWaveform(): void {
    const g = this.waveform;
    const { x0, y0, w, h } = this.waveBox();
    g.clear();
    for (let i = 0; i < this.wavePoints.length; i++) {
      const x = x0 + (i / (this.wavePoints.length - 1)) * w;
      const y = y0 + h - this.wavePoints[i]! * h;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.stroke({ width: 2, color: 0xffffff, alpha: 0.9 });
  }

  private buildButton(btn: Container, dir: 1 | -1): void {
    const g = new Graphics();
    g.circle(0, 0, 34).fill({ color: 0xffffff, alpha: 0.16 });
    g.circle(0, 0, 34).stroke({ width: 2, color: 0xffffff, alpha: 0.35 });
    const chevron = new Graphics();
    const s = 13;
    chevron
      .moveTo(-s, dir === 1 ? s * 0.55 : -s * 0.55)
      .lineTo(0, dir === 1 ? -s * 0.55 : s * 0.55)
      .lineTo(s, dir === 1 ? s * 0.55 : -s * 0.55)
      .stroke({ width: 5, color: 0xffffff, cap: "round", join: "round" });
    btn.addChild(g, chevron);
    btn.eventMode = "static";
    btn.cursor = "pointer";
    // Generous hit area for thumbs.
    btn.hitArea = { contains: (x: number, y: number) => Math.hypot(x, y) < 56 };
    btn.on("pointerdown", () => {
      btn.scale.set(0.9);
      this.onLaneChange(dir);
    });
    btn.on("pointerup", () => btn.scale.set(1));
    btn.on("pointerupoutside", () => btn.scale.set(1));
  }
}
